import {isEmpty} from "es-toolkit/compat";
import {uniqBy} from "es-toolkit/compat";

import {generateInitialState} from "@/helpers/generateInitialState";
import {createSlice, isAnyOf} from "@reduxjs/toolkit";
import {resultsInitialState} from "../initialStates";
import {
  loadResults,
  loadMoreResults,
  loadMarkerHistory,
  loadHealthCategories,
  loadSupplementsRecommended,
  getPdf,
  getMyFormPdf,
} from "./resultsActions";

const initialState = await generateInitialState("results", resultsInitialState);

const resultsSlice = createSlice({
  name: "results",
  initialState,
  reducers: {
    updateCustomerResults: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.data = [...(action.payload?.data || [])];
      state.metadata = !isEmpty(state.metadata)
        ? {
            ...state.metadata,
            currentPage: action.payload?.metadata?.currentPage,
            totalPages: action.payload?.metadata?.totalPages,
            total: action.payload?.metadata?.total,
          }
        : action.payload?.metadata;
    },
    updateMarkerHistory: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.history = [...action.payload];
    },
    updateHealthCategoriesState: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.healthCategories = [...action.payload];
    },
    updateSupplementsState: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.supplements = [...action.payload];
    },
    setHealthCategorySelected: (state, action) => {
      const categoryId = action.payload;

      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.healthCategorySelected = categoryId;
    },
    updateResultsMetadata: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.metadata = action.payload || {};
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadResults.fulfilled, (state, action) => {
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
        state.data = action.payload?.data || [];
        state.metadata = action.payload?.metadata || {};
      })
      .addCase(getPdf.fulfilled, (state, action) => {
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
        state.pdf = action.payload;
      })
      .addCase(loadMarkerHistory.fulfilled, (state, action) => {
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
        state.history = [...action.payload];
      })
      .addCase(loadHealthCategories.fulfilled, (state, action) => {
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
        state.healthCategories = [...action.payload];
      })
      .addCase(loadSupplementsRecommended.fulfilled, (state, action) => {
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
        state.supplements = [...action.payload];
      })
      .addCase(loadMoreResults.fulfilled, (state, action) => {
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
        state.metadata = action.payload.metadata;
        state.data = uniqBy([...state.data, ...action.payload.data], "orderId");
      })
      .addMatcher(
        isAnyOf(
          loadResults.pending,
          loadMoreResults.pending,
          loadMarkerHistory.pending,
          loadHealthCategories.pending,
          loadSupplementsRecommended.pending,
          getPdf.pending,
          getMyFormPdf.pending,
        ),
        (state, action) => {
          state.hasFailed = false;
          state.isFetching = true;
          state.errorMsg = "";
        },
      )
      .addMatcher(
        isAnyOf(
          loadResults.rejected,
          loadMoreResults.rejected,
          loadMarkerHistory.rejected,
          loadHealthCategories.rejected,
          loadSupplementsRecommended.rejected,
          getPdf.rejected,
          getMyFormPdf.rejected,
        ),
        (state, action) => {
          const {message = "Something went wrong"} = action.payload || {};

          state.hasFailed = true;
          state.isFetching = false;
          state.errorMsg = message;
        },
      );
  },
  selectors: {
    resultsDataSelector: state => state.data,
    resultsMetadataSelector: state => state.metadata,
    resultsHistorySelector: state => state.history,
    resultsHealthCategoriesSelector: state => state.healthCategories,
    resultsSupplementsSelector: state => state.supplements,
    resultsHealthCategorySelectedSelector: state => state.healthCategorySelected,
    resultsStatusSelector: state => ({
      isFetching: state.isFetching,
      hasFailed: state.hasFailed,
      errorMsg: state.errorMsg,
    }),
  },
});

export const {
  setHealthCategorySelected,
  updateCustomerResults,
  updateHealthCategoriesState,
  updateMarkerHistory,
  updateResultsMetadata,
  updateSupplementsState,
} = resultsSlice.actions;

export const {
  resultsDataSelector,
  resultsMetadataSelector,
  resultsHistorySelector,
  resultsHealthCategoriesSelector,
  resultsSupplementsSelector,
  resultsStatusSelector,
  resultsHealthCategorySelectedSelector,
} = resultsSlice.selectors;

export default resultsSlice.reducer;
